import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/dafum99tto0ceqqnhi2g";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐检测：若被机器人填写则静默当作成功处理
    const honeypot = (fd.get("company_alt") as string | null) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("company_alt");

    if (fd.get("message") && (fd.get("message") as string).length > 500) {
      setFormError("留言内容不能超过 500 字，请精简后再提交。");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      if (res.ok && parsed?.code === "OK" && !/spam/i.test(serverMsg)) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(/spam/i.test(serverMsg) ? "提交内容疑似异常，请稍后再试。" : serverMsg || "提交失败，请稍后再试。");
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，请稍后再试。");
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-background-100 scroll-mt-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-primary-700 p-6 md:p-10 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute inset-0 opacity-15">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20minimal%20ink%20pattern%20on%20dark%20red%20paper%2C%20editorial%20texture%2C%20subtle%20fine%20lines%20and%20dots%2C%20warm%20mood&width=800&height=900&seq=contact-bg-41&orientation=portrait"
                alt="联系背景"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative z-10">
              <h2 className="font-heading text-2xl md:text-3xl font-black text-background-50">
                有想回看的脉络？告诉我们
              </h2>
              <p className="mt-3 text-primary-100 text-sm md:text-base leading-relaxed max-w-sm">
                你可以点播希望编辑部关注的话题方向，或对我们整理的内容提出补充与纠错。我们会认真对待每一条反馈。
              </p>
              <div className="mt-6 flex flex-col gap-3 text-primary-50/90 text-sm">
                <span className="flex items-center gap-2"><i className="ri-mail-line text-accent-300"></i> feedback@fortyone-liao.com</span>
                <span className="flex items-center gap-2"><i className="ri-community-line text-accent-300"></i> 工作日持续跟进整理与更新</span>
              </div>
            </div>
          </div>

          <div className="bg-background-50 p-6 md:p-10">
            <form id="feedback-form" data-readdy-form="fortyone-feedback" onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-xs font-semibold text-foreground-800">你的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className="px-3.5 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-xs font-semibold text-foreground-800">常用邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="px-3.5 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="topic" className="text-xs font-semibold text-foreground-800">反馈类型</label>
                <select
                  id="topic"
                  name="topic"
                  className="px-3.5 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
                >
                  <option value="点播话题">点播话题方向</option>
                  <option value="补充线索">补充时间线索</option>
                  <option value="纠错反馈">内容纠错</option>
                  <option value="其他">其他建议</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="message" className="text-xs font-semibold text-foreground-800">具体内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="请描述你关注的方向或需要反馈的内容（500 字以内）"
                  className="px-3.5 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
                ></textarea>
              </div>

              {/* 蜜罐字段（仅供反垃圾，真实用户无需填写） */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="fortyone-honeypot"
              />

              {status === "error" && (
                <p role="alert" className="text-sm text-primary-600 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                  {formError}
                </p>
              )}
              {status === "success" && (
                <p className="text-sm text-secondary-700 bg-secondary-100 border border-secondary-200 rounded-md px-3 py-2">
                  已收到你的反馈，谢谢！我们会据此安排后续整理方向。
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-bold whitespace-nowrap transition-colors disabled:opacity-60 cursor-pointer"
              >
                {status === "loading" ? "提交中…" : "提交反馈"}
                <i className="ri-send-plane-line text-base"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}